import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  KeyRound,
  Menu,
  Phone,
  Search,
  User as UserIcon,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { VerifiedBadge } from "@/components/verified-badge";
import { azError } from "@/lib/az-errors";
import { fetchPresence, isOnline } from "@/lib/presence";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import type { AdminUser } from "@/lib/admin";
import { adjustBalance, fetchAllUsers, fetchListingsByUser, setUserAdmin, setUserBlocked, setUserModerator, setUserStore, setUserVerified } from "@/lib/admin";
import { listAuthUsers, setUserPassword } from "@/lib/admin-users.functions";

export const Route = createFileRoute("/_authenticated/admin/istifadeciler")({
  component: Page,
});

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("az-AZ", { day: "2-digit", month: "2-digit", year: "numeric" });

function Page() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [blockReasons, setBlockReasons] = useState<Record<string, string>>({});
  const [pwTarget, setPwTarget] = useState<{ id: string; login: string } | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [adsDialog, setAdsDialog] = useState<{ userId: string; login: string } | null>(null);

  const usersQuery = useQuery({ queryKey: ["admin-users"], queryFn: fetchAllUsers });
  const authFn = useServerFn(listAuthUsers);
  const authQuery = useQuery({ queryKey: ["admin-auth-users"], queryFn: () => authFn({ data: undefined as never }) });
  const passwordFn = useServerFn(setUserPassword);
  const adsQuery = useQuery({
    queryKey: ["admin-user-ads", adsDialog?.userId],
    queryFn: () => fetchListingsByUser(adsDialog!.userId),
    enabled: !!adsDialog?.userId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
  };

  const blockMutation = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) => setUserBlocked(id, blocked),
    onSuccess: (_d, v) => {
      toast.success(v.blocked ? "İstifadəçi bloklandı" : "Blok götürüldü");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, admin }: { id: string; admin: boolean }) => setUserAdmin(id, admin),
    onSuccess: (_d, v) => {
      toast.success(v.admin ? "Admin hüququ verildi" : "Admin hüququ alındı");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const moderatorMutation = useMutation({
    mutationFn: ({ id, moderator }: { id: string; moderator: boolean }) => setUserModerator(id, moderator),
    onSuccess: (_d, v) => {
      toast.success(v.moderator ? "Moderator hüququ verildi" : "Moderator hüququ alındı");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const balanceMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      adjustBalance(id, amount, "Admin tərəfindən balans dəyişikliyi"),
    onSuccess: () => {
      toast.success("Balans yeniləndi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const storeMutation = useMutation({
    mutationFn: ({ u, enabled }: { u: AdminUser; enabled: boolean }) => setUserStore(u, enabled),
    onSuccess: (_d, v) => {
      toast.success(v.enabled ? "İstifadəçi təsdiqlənmiş mağaza oldu" : "Mağaza statusu ləğv edildi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const verifyMutation = useMutation({
    mutationFn: ({ id, verified }: { id: string; verified: boolean }) => setUserVerified(id, verified),
    onSuccess: (_d, v) => {
      toast.success(v.verified ? "Yoxlanmış satıcı nişanı verildi" : "Nişan götürüldü");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const passwordMutation = useMutation({
    mutationFn: (v: { userId: string; password: string }) => passwordFn({ data: v }),
    onSuccess: () => {
      toast.success("Şifrə dəyişdirildi");
      setPwTarget(null);
      setNewPassword("");
    },
    onError: (e) => toast.error(azError(e)),
  });

  const emails = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const a of authQuery.data ?? []) m.set(a.id, a.email);
    return m;
  }, [authQuery.data]);

  const rows = (usersQuery.data ?? []).map((u) => {
    const email = emails.get(u.id) ?? null;
    const login = (email ? email.split("@")[0] : u.full_name) ?? "istifadeci";
    return { ...u, email, login };
  });

  const ids = rows.map((r) => r.id);
  const presenceQuery = useQuery({
    queryKey: ["admin-users-presence", ids.length],
    queryFn: () => fetchPresence(ids),
    enabled: ids.length > 0,
    refetchInterval: 30_000,
  });
  const onlineOf = (id: string) => isOnline(presenceQuery.data?.[id]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((u) =>
        `${u.login} ${u.email ?? ""} ${u.full_name ?? ""} ${u.phone ?? ""}`.toLowerCase().includes(q),
      )
    : rows;

  return (
    <div>
      <AdminPageHeader
        title="İstifadəçilər"
        description="Login, rol, balans və şifrə idarəsi"
      />

      <div className="relative mb-3">
        <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Login, e-poçt, ad və ya telefon ilə axtar..."
          className="h-12 rounded-2xl pl-11"
        />
      </div>

      <p className="mb-4 rounded-2xl border border-border bg-muted/50 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
        Təhlükəsizlik səbəbindən mövcud şifrələr heç kimə, o cümlədən adminə göstərilmir. İstifadəçiyə
        kömək lazımdırsa, <b>“Şifrəni dəyiş”</b> düyməsi ilə yeni şifrə təyin edə bilərsiniz.
      </p>

      <div className="grid gap-3">
        {filtered.map((u) => {
          const role = u.is_admin ? "admin" : u.is_moderator ? "moderator" : "user";
          const accountNo = 10000 + (parseInt(u.id.replace(/-/g, "").slice(0, 6), 16) % 90000);
          return (
            <div
              key={u.id}
              className="w-full max-w-full overflow-hidden rounded-2xl border border-border bg-card p-4 text-foreground shadow-card"
            >
              {/* Sətir 1: ad, e-poçt + rol nişanı */}
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <div className="min-w-0">
                  <Link
                    to="/istifadeci/$id"
                    params={{ id: u.id }}
                    preload="intent"
                    className="block truncate text-sm font-bold text-info hover:underline"
                  >
                    {u.full_name || u.login}
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">{u.email ?? "—"}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Hesab №: {accountNo}</p>
                  <p className="text-xs text-muted-foreground">· {fmtDate(u.created_at)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    👣{" "}
                    <button
                      onClick={() => setAdsDialog({ userId: u.id, login: u.login })}
                      className="cursor-pointer border-none bg-transparent p-0 font-semibold text-info underline"
                    >
                      Elanlar: {u.listings}
                    </button>
                    <span className="mx-1">·</span>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 font-semibold",
                        onlineOf(u.id) ? "text-green-600" : "text-muted-foreground",
                      )}
                    >
                      <span
                        className={cn(
                          "size-1.5 rounded-full",
                          onlineOf(u.id) ? "bg-green-500" : "bg-gray-400",
                        )}
                      />
                      {onlineOf(u.id) ? "Online" : "Offline"}
                    </span>
                  </p>
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold",
                    u.blocked
                      ? "bg-red-600 text-white"
                      : role === "admin"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {u.blocked ? "Bloklanıb" : role}
                </span>
              </div>

              {/* Sətir 2: rol seçimi */}
              <div className="mt-3">
                <select
                  value={role}
                  disabled={u.id === user?.id || roleMutation.isPending || moderatorMutation.isPending}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === role) return;
                    if (next === "admin") roleMutation.mutate({ id: u.id, admin: true });
                    else {
                      if (u.is_admin) roleMutation.mutate({ id: u.id, admin: false });
                      if (next === "moderator" && !u.is_moderator)
                        moderatorMutation.mutate({ id: u.id, moderator: true });
                      if (next === "user" && u.is_moderator)
                        moderatorMutation.mutate({ id: u.id, moderator: false });
                    }
                  }}
                  className="h-10 w-32 rounded-xl border border-border bg-background px-3 text-sm font-medium"
                >
                  <option value="user">İstifadəçi</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Sətir 3: şifrə + digər əməliyyatlar */}
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => {
                    setPwTarget({ id: u.id, login: u.login });
                    setNewPassword("");
                  }}
                  className="flex h-10 items-center gap-2 rounded-full border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-muted"
                >
                  <KeyRound size={15} className="text-muted-foreground" /> Şifrəni dəyiş
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="grid size-10 shrink-0 place-items-center rounded-full border border-border bg-background transition-colors hover:bg-muted"
                      aria-label="Əməliyyatlar"
                    >
                      <Menu size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={6}
                    collisionPadding={8}
                    className="w-56 rounded-2xl border-border/70 p-1.5 shadow-xl"
                  >
                    <DropdownMenuItem asChild className="rounded-xl px-2.5 py-2.5 text-sm">
                      <Link to="/istifadeci/$id" params={{ id: u.id }}>
                        <UserIcon className="mr-2 size-4 shrink-0 text-muted-foreground" /> Profilə bax
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setAdsDialog({ userId: u.id, login: u.login })}
                      className="rounded-xl px-2.5 py-2.5 text-sm"
                    >
                      Elanlarına bax ({u.listings})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        if (u.store_id) {
                          if (window.confirm(`${u.login} adlı istifadəçinin mağazası birdəfəlik silinsin?`))
                            storeMutation.mutate({ u, enabled: false });
                        } else {
                          storeMutation.mutate({ u, enabled: true });
                        }
                      }}
                      disabled={storeMutation.isPending}
                      className="rounded-xl px-2.5 py-2.5 text-sm"
                    >
                      {u.store_id ? "Mağazanı sil" : "Mağaza et"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1" />
                    <div className="flex items-center gap-1.5 px-1.5 py-1">
                      <Input
                        value={amounts[u.id] ?? ""}
                        onChange={(e) => setAmounts((a) => ({ ...a, [u.id]: e.target.value }))}
                        placeholder="AZN"
                        inputMode="decimal"
                        className="h-9 flex-1 rounded-xl border-border px-2 text-xs"
                      />
                      <button
                        disabled={balanceMutation.isPending}
                        onClick={() => {
                          const amount = Number(amounts[u.id]);
                          if (!amount) {
                            toast.error("Məbləği daxil edin");
                            return;
                          }
                          balanceMutation.mutate({ id: u.id, amount });
                        }}
                        className="grid size-9 shrink-0 place-items-center rounded-xl bg-muted transition-colors hover:bg-muted/70"
                        aria-label="Balans əlavə et"
                      >
                        <Wallet size={15} />
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Yoxlanmış satıcı */}
              <div className="mt-2 flex items-center justify-between gap-2 rounded-2xl border border-dashed border-border p-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <VerifiedBadge verified compact /> Yoxlanmış satıcı
                </span>
                <button
                  disabled={verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate({ id: u.id, verified: !u.verified })}
                  className={cn(
                    "h-9 shrink-0 rounded-full px-4 text-xs font-bold transition-colors",
                    u.verified
                      ? "bg-info text-white hover:bg-info/90"
                      : "border border-border bg-background hover:bg-muted",
                  )}
                >
                  {u.verified ? "Nişanı götür" : "Nişan ver"}
                </button>
              </div>

              {/* Sətir 4: bloklama */}
              <div className="mt-2 flex items-center gap-2 rounded-2xl border border-dashed border-border p-2">
                <Input
                  value={blockReasons[u.id] ?? ""}
                  onChange={(e) => setBlockReasons((r) => ({ ...r, [u.id]: e.target.value }))}
                  placeholder="Bloklama səbəbi"
                  className="h-10 flex-1 rounded-full border-border bg-background px-4 text-sm"
                />
                <button
                  disabled={blockMutation.isPending}
                  onClick={() => blockMutation.mutate({ id: u.id, blocked: !u.blocked })}
                  className={cn(
                    "flex h-10 shrink-0 items-center gap-1.5 rounded-full px-5 text-sm font-bold text-white transition-colors",
                    u.blocked ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700",
                  )}
                >
                  🚫 {u.blocked ? "Blokdan çıxar" : "Blokla"}
                </button>
              </div>
            </div>
          );
        })}


        {filtered.length === 0 && (
          <div className="rounded-3xl border border-border bg-card py-10 text-center text-sm text-muted-foreground">
            İstifadəçi tapılmadı.
          </div>
        )}
      </div>

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Şifrəni dəyiş</DialogTitle>
            <DialogDescription>
              <b>{pwTarget?.login}</b> üçün yeni şifrə təyin edin (ən azı 6 simvol).
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Yeni şifrə"
            className="h-11 rounded-2xl"
          />
          <Button
            className="h-11 w-full rounded-2xl font-bold"
            disabled={passwordMutation.isPending}
            onClick={() => {
              if (newPassword.trim().length < 6) {
                toast.error("Şifrə ən azı 6 simvol olmalıdır");
                return;
              }
              if (pwTarget) passwordMutation.mutate({ userId: pwTarget.id, password: newPassword.trim() });
            }}
          >
            {passwordMutation.isPending ? "Gözləyin..." : "Yadda saxla"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adsDialog} onOpenChange={(o) => !o && setAdsDialog(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle>{adsDialog?.login} — elanlar</DialogTitle>
            <DialogDescription>
              İstifadəçinin yerləşdirdiyi elanların siyahısı. Başlığa klikləyərək detala baxa bilərsiniz.
            </DialogDescription>
          </DialogHeader>
          {adsQuery.isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Yüklənir...</div>
          ) : (adsQuery.data ?? []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
              Bu istifadəçinin hələ elanı yoxdur.
            </div>
          ) : (
            <div className="grid gap-3">
              {(adsQuery.data ?? []).map((ad) => (
                <Link key={ad.id} to="/elan/$id" params={{ id: ad.id }}>
                  <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 transition-colors hover:bg-muted/60">
                    <div className="size-16 shrink-0 overflow-hidden rounded-xl bg-muted">
                      {ad.images?.[0] ? (
                        <img
                          src={ad.images[0]}
                          alt={ad.title}
                          className="size-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="grid size-full place-items-center text-xs text-muted-foreground">
                          Şəkil yoxdur
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold">{ad.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {ad.price.toLocaleString("az-AZ")} AZN · {ad.category} · {ad.city}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {ad.status === "active" ? "Aktiv" : ad.status === "pending" ? "Gözləyir" : ad.status === "rejected" ? "İmtina" : "Satılıb"}
                        {ad.badge !== "none" && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                            {ad.badge === "vip" ? "VIP" : "Premium"}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
